import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { fetchOrders } from '../services/orders.services';
import { Button } from '@/components/ui/button';
import { Loader2, ShoppingCart, ChevronDown, ChevronRight, PackageCheck } from 'lucide-react';
import ReceiveOrderModal from './ReceiveOrderModal';

const estadoBadge = (estado) => {
  const map = {
    Solicitado: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    Procesado: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    Cancelado: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    Pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  };
  const cls = map[estado] || 'bg-gray-100 text-gray-800';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{estado}</span>;
};

const OrdersTable = ({ idLavanderia, refreshKey = 0, onOpenOrderModal, onOrderReceived }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const load = useCallback(async () => {
    if (!idLavanderia) return;
    setLoading(true);
    try {
      const res = await fetchOrders({ lavanderiaId: idLavanderia });
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('Error obteniendo pedidos:', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [idLavanderia]);

  useEffect(() => { load(); }, [load, idLavanderia, refreshKey]);

  const orders = useMemo(() => rows.map(r => ({
    id: r._id || r.id,
    _raw: r,
    numeroPedido: r.numeroPedido,
    fecha: r.fecha ? new Date(r.fecha) : null,
    proveedor: r?.proveedor?.nombre || '-',
    items: Array.isArray(r?.items) ? r.items : [],
    totalLitros: Number(r?.totalLitros ?? 0),
    estado: r?.estado || 'Solicitado',
    observaciones: r?.observaciones || '',
    // Compatibilidad con pedidos viejos (single product)
    productoLegacy: r?.producto?.nombre || '',
    cantidadLegacy: Number(r?.cantidad ?? 0),
    litrosTotalLegacy: Number(r?.litrosTotal ?? 0),
  })), [rows]);

  const handleOpenReceive = (order) => {
    setSelectedOrder(order);
    setReceiveModalOpen(true);
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Pedidos</CardTitle>
        <div className="flex items-center gap-2">
          {onOpenOrderModal && (
            <Button size="sm" onClick={onOpenOrderModal}>
              <ShoppingCart className="h-4 w-4 mr-1" /> Hacer pedido
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Cargando</>) : 'Actualizar'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead># Pedido</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead className="text-right">Productos</TableHead>
                <TableHead className="text-right">Total litros</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground">Sin pedidos.</TableCell>
                </TableRow>
              ) : (
                orders.map(order => {
                  const hasItems = order.items.length > 0;
                  const isExpanded = expandedIds.has(order.id);
                  const productCount = hasItems ? order.items.length : (order.productoLegacy ? 1 : 0);
                  const totalLitros = hasItems ? order.totalLitros : order.litrosTotalLegacy;
                  return (
                    <React.Fragment key={order.id || order.numeroPedido}>
                      <TableRow className="cursor-pointer hover:bg-muted/30" onClick={() => hasItems && toggleExpand(order.id)}>
                        <TableCell className="px-2">
                          {hasItems ? (
                            isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          ) : null}
                        </TableCell>
                        <TableCell className="font-medium">{order.numeroPedido}</TableCell>
                        <TableCell>{order.fecha ? order.fecha.toLocaleDateString('es-CO') : '-'}</TableCell>
                        <TableCell>{order.proveedor}</TableCell>
                        <TableCell className="text-right">{productCount}</TableCell>
                        <TableCell className="text-right">{totalLitros.toLocaleString()}</TableCell>
                        <TableCell>{estadoBadge(order.estado)}</TableCell>
                        <TableCell>
                          {order.estado === 'Solicitado' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={(e) => { e.stopPropagation(); handleOpenReceive(order); }}
                            >
                              <PackageCheck className="h-3.5 w-3.5 mr-1" /> Recibir
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {isExpanded && hasItems && order.items.map((it, idx) => (
                        <TableRow key={`${order.id}-item-${idx}`} className="bg-muted/10">
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell colSpan={2} className="text-sm text-muted-foreground pl-6">
                            {it?.producto?.nombre || '-'}
                          </TableCell>
                          <TableCell className="text-right text-sm">{Number(it?.cantidad ?? 0)}</TableCell>
                          <TableCell className="text-right text-sm">{Number(it?.litrosTotal ?? 0).toLocaleString()}</TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
      <ReceiveOrderModal
        open={receiveModalOpen}
        onOpenChange={setReceiveModalOpen}
        order={selectedOrder}
        onReceived={() => {
          load();
          onOrderReceived && onOrderReceived();
        }}
      />
    </Card>
  );
};

export default OrdersTable;
